import { useCallback, useEffect, useMemo, useRef } from "react";
import { MdInventory2, MdStorefront } from "react-icons/md";
import { BrandLayout, Feature } from "__generated__/gql/graphql";
import { asyncVoid } from "utils/asyncVoidHandler";
import {
  useCurrentBrandData,
  useCurrentBrandId,
  useUserHasFeature,
} from "utils/currentUserHooks";
import { BRAND_RETAILERS_INDEX, PRODUCTS_INDEX } from "utils/links";
import { usePatchCurrentBrandMetadata } from "utils/usePatchBrandMetadata";
import {
  getOnboardingFlowStatus,
  ONBOARDING_FLOW_METADATA_KEY,
  OnboardingFlowStatus,
  type OnboardingFlowPatch,
} from "./metadata";
import useBrandOnboardingStatus from "./useBrandOnboardingStatus";

type Options = {
  skip?: boolean;
};

export default function useCurrentBrandOnboarding({ skip }: Options = {}) {
  const brand = useCurrentBrandData();
  const brandId = useCurrentBrandId();
  const hasOnboardingFeature = useUserHasFeature(Feature.BRAND_ONBOARDING);
  const hasPatchedCompletedRef = useRef(false);
  const patchCurrentBrandMetadata = usePatchCurrentBrandMetadata();

  const status = getOnboardingFlowStatus(brand?.metadata);
  const isStoredCompleted = status === OnboardingFlowStatus.COMPLETED;
  const isVisible =
    !skip &&
    hasOnboardingFeature === true &&
    !!brandId &&
    !!brand &&
    status !== undefined &&
    status !== OnboardingFlowStatus.DISMISSED;

  const {
    completedCount: statusCompletedCount,
    hasProducts,
    hasRetailers,
    isReady: isStatusReady,
    loading: isStatusLoading,
    targetStatus,
  } = useBrandOnboardingStatus({
    brandId,
    isAdmin: false,
    layout: brand?.layout,
    skip: !isVisible || isStoredCompleted,
  });
  const isOnboardingStatusReady = isStoredCompleted || isStatusReady;
  const isCompleted =
    isStoredCompleted || targetStatus === OnboardingFlowStatus.COMPLETED;
  const shouldShowSetupGuide = isVisible && !isCompleted;

  const patchOnboardingFlowStatus = useCallback(
    (onboardingFlowPatch: OnboardingFlowPatch) => {
      asyncVoid(
        patchCurrentBrandMetadata({
          [ONBOARDING_FLOW_METADATA_KEY]: onboardingFlowPatch,
        }),
      );
    },
    [patchCurrentBrandMetadata],
  );

  useEffect(() => {
    if (
      !isVisible ||
      isStatusLoading ||
      status === OnboardingFlowStatus.COMPLETED ||
      !isCompleted
    ) {
      return;
    }
    if (hasPatchedCompletedRef.current) return;
    hasPatchedCompletedRef.current = true;
    patchOnboardingFlowStatus({
      status: OnboardingFlowStatus.COMPLETED,
      completedAt: new Date().toISOString(),
    });
  }, [
    isStatusLoading,
    isVisible,
    patchOnboardingFlowStatus,
    status,
    isCompleted,
  ]);

  const steps = useMemo(() => {
    const isAnalyticsLayout = brand?.layout === BrandLayout.ANALYTICS;
    const productStep = {
      id: "products",
      title: "Add Products",
      description: isAnalyticsLayout
        ? "Add your SKUs so analytics imports can match sales and inventory data to the right products."
        : "Upload your SKUs with UPCs, sizes, and attributes — the data we’ll use to fill retailer forms.",
      href: PRODUCTS_INDEX,
      isComplete: hasProducts,
      IconComponent: MdInventory2,
    };

    if (isAnalyticsLayout) return [productStep];

    return [
      productStep,
      {
        id: "retailers",
        title: "Add Retailers",
        description:
          "Pick the retailers you sell to from our directory of 500+ chains and independents.",
        href: {
          pathname: BRAND_RETAILERS_INDEX,
          query: { openDirectory: "1" },
        },
        isComplete: hasRetailers,
        IconComponent: MdStorefront,
      },
    ];
  }, [brand?.layout, hasProducts, hasRetailers]);
  const completedCount = isStoredCompleted
    ? steps.length
    : statusCompletedCount;

  const dismiss = useCallback(() => {
    patchOnboardingFlowStatus({
      status: OnboardingFlowStatus.DISMISSED,
      dismissedAt: new Date().toISOString(),
      dismissalReason: "userDismissed",
    });
  }, [patchOnboardingFlowStatus]);

  return {
    completedCount,
    dismiss,
    isCompleted,
    isStatusReady: isOnboardingStatusReady,
    isStatusLoading,
    isVisible,
    shouldShowSetupGuide,
    steps,
  };
}
